import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { AppButton } from 'components/AppButton';
import { LanguageSwitcher } from 'components/language-switcher';
import { getLocaleLanguage, getTrans, getTransWithParams } from 'modules/i18n';
import { ApiError } from 'utils/api';
import { findRenderer } from './blocks/registry';
import {
  inputBlocksOf,
  isBlockAnswered,
  progressPercentOf,
  resumeIndexOf
} from './answers';
import { FormAnswers, GuestFormResponse } from './api-types';
import { answerRejectionOf, isTerminalFormError } from './formErrors';
import { useGuestFormSession } from './GuestFormSessionContext';
import { isLanguageSwitchable, resolveLocale } from './localizedText';
import { useSubmitGuestForm } from './query';
import {
  signalActionBar,
  signalAppFrame,
  signalMeasure,
  signalMono,
  signalNote,
  signalPrimaryAction,
  signalProgressSegment,
  signalProgressTrack,
  signalScreen,
  signalScrollArea,
  signalTopBar
} from './signalStyles';
import { useFormAutosave } from './useFormAutosave';

/**
 * One question per screen, which is the normative interaction for the talent flow (catalog §12.4).
 * It is not decoration: a phone screen shows one question honestly, and it makes "answer, see the
 * consequence, move on" the rhythm instead of a wall of fields judged at the end.
 *
 * Position is deliberately NOT in the URL. A refresh re-redeems and resumes at the first
 * unanswered question anyway, so an index in the address bar would add a hand-editable,
 * out-of-range input for no gain.
 *
 * The frame is three regions — progress, question, action — and only the middle one scrolls, so the
 * progress and the primary action stay exactly where a thumb left them while the question moves.
 *
 * Structure, spacing and rules are the prototype's, not a reinterpretation of it. Two earlier passes
 * adjusted this layout by argument — removing the dividers, centring the question — and both made it
 * worse, because the composition is a whole and the parts were being reasoned about one at a time.
 */
/** Where one progress segment sits relative to the screen currently on show. */
const segmentState = (
  position: number,
  current: number
): 'done' | 'current' | 'todo' => {
  if (position === current) return 'current';
  return position < current ? 'done' : 'todo';
};

export const FormRunner = ({ data }: { data: GuestFormResponse }) => {
  const {
    sessionToken,
    getSessionToken,
    recoverSession,
    routeError,
    markSubmitted
  } = useGuestFormSession();
  const definition = data.definition;
  const blocks = useMemo(() => definition?.blocks ?? [], [definition]);
  // Offered only for a form published in BOTH German and English — see `isLanguageSwitchable`.
  const canSwitchLanguage = isLanguageSwitchable(definition);

  /**
   * The locale to read the definition in — resyncable, not a pure derivation of `definition`.
   *
   * `resolveLocale` also reads the app's CURRENT locale, so the obvious `useMemo(() =>
   * resolveLocale(definition), [definition])` goes stale the instant that changes without
   * `definition` changing — exactly what happens here: `LanguageSwitcher` flips the app's locale
   * before it ever calls back into this component. `handleLanguageSelect` below re-syncs this
   * explicitly, the moment a switch starts, rather than waiting on a `definition` change that may
   * never come.
   */
  const baseLocale = useMemo(() => resolveLocale(definition), [definition]);
  const [locale, setLocale] = useState(baseLocale);
  useEffect(() => {
    setLocale(baseLocale);
  }, [baseLocale]);

  /**
   * The questions an operator did not release when reopening a submitted response: shown read-only, never editable.
   * Empty for a response that was never reopened, and for a backend that does not send the field.
   */
  const lockedIds = useMemo(
    () => new Set(data.lockedBlockInstanceIds ?? []),
    [data.lockedBlockInstanceIds]
  );
  const isReopened = lockedIds.size > 0;

  const [answers, setAnswers] = useState<FormAnswers>(data.answers ?? {});
  const [index, setIndex] = useState(() =>
    resumeIndexOf(definition, data.answers ?? {}, lockedIds)
  );
  const [rejection, setRejection] = useState<string | null>(null);
  // Which way the last move went, so the next screen enters from the side it came from.
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const headingRef = useRef<HTMLDivElement | null>(null);

  /**
   * Submission lock — synchronous, because the button's `disabled` only takes effect on the next
   * render and the finalize sequence starts with an awaited network write.
   *
   * Submit stores the answer set it captured and then asks the server to judge what is STORED. So
   * anything that changes the answers after that capture is a lie waiting to happen: the edit is on
   * screen, it is not in what was submitted, and nothing anywhere says so. The ref closes that
   * window for both ways in — a second tap, and an edit to the question still on screen — and it has
   * to be a ref rather than state for the same reason the guest CV flow uses one: state updates
   * arrive a render too late to stop either.
   *
   * `isFinalizing` is the visible half. It replaces React Query's `isLoading`, which only turns true
   * once the mutation itself starts — i.e. after the save it waits on, leaving the whole write
   * looking idle.
   */
  const submittingRef = useRef(false);
  const [isFinalizing, setIsFinalizing] = useState(false);

  /**
   * The same lock, for the same reason, around a language switch's flush.
   *
   * Nothing previously stopped an edit or a submit from landing while `handleLanguageSelect`'s own
   * `flushSave` was still in flight — two unsynchronised writes with no ordering guarantee between
   * them, and whichever response landed last would win regardless of which was actually newer.
   */
  const switchingLanguageRef = useRef(false);
  const [isSwitchingLanguage, setIsSwitchingLanguage] = useState(false);

  /**
   * The block on screen is mid-request (an upload or a removal) and said so through `onBusyChange`.
   *
   * Navigation and submit wait for it, because the answer this runner holds is still the old one
   * until the request settles: Next would stay enabled on a stale answer, and leaving would drop
   * the result of the very request the respondent just made. The block clears it on unmount, so a
   * block that goes away mid-request can never leave the form locked.
   */
  const [blockBusy, setBlockBusy] = useState(false);

  const { scheduleSave, flushSave, saveState } = useFormAutosave(
    data.formRef,
    sessionToken ?? '',
    recoverSession,
    routeError
  );
  const { mutateAsync: submit } = useSubmitGuestForm(data.formRef);

  /**
   * Finalize, and if the session lapsed on the way, re-redeem the still-valid link once and retry.
   *
   * The save that runs immediately before this already recovers on its own, and that was the gap:
   * it would mint a fresh token, publish it through state, and then submit would go out carrying the
   * expired one this render had captured — surfacing as "submitting failed" on a link that was
   * perfectly good. Reading the token at call time closes most of it; retrying once closes the rest,
   * for the case where the session lapses between the save and the submit.
   *
   * A genuinely dead invitation leaves `recoverSession` to select the terminal screen, and the
   * original error is re-thrown for `handleSubmit` to classify.
   *
   * No token at all is treated as a lapsed one rather than sent as an empty credential — see the
   * comment at that branch.
   */
  const submitWithRecovery = useCallback(async () => {
    /**
     * Re-redeem the link still in the URL and submit with what it mints.
     *
     * Rethrows `cause` when the invitation is genuinely dead. `recoverSession` has already selected
     * the terminal screen by then, but this still has to fail: returning quietly would let
     * `handleSubmit` go on to call `markSubmitted` for a form that was never submitted.
     */
    const submitAfterRecovery = async (cause: unknown) => {
      const freshToken = await recoverSession();
      if (!freshToken) {
        throw cause;
      }
      await submit(freshToken);
    };

    const token = getSessionToken();
    if (!token) {
      // No live token is the same problem as a lapsed one, so it gets the same repair. Coercing it
      // to '' would send `Bearer ` and leave the server to decide what an empty credential means —
      // which is the mistake this change removed one version of, in dropping the casts that would
      // have sent `Bearer undefined`. Doing it again with a different empty value is no better.
      await submitAfterRecovery(
        new ApiError({ code: 'error.guest.session-expired', values: {} })
      );
      return;
    }

    try {
      await submit(token);
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.code === 'error.guest.session-expired'
      ) {
        await submitAfterRecovery(error);
        return;
      }
      throw error;
    }
  }, [submit, getSessionToken, recoverSession]);

  const block = blocks[index];
  const renderer = block ? findRenderer(block) : undefined;
  const progress = progressPercentOf(definition, answers);

  // Counted over the blocks that actually ask something, so interleaving statements never makes
  // "question 3 of 5" jump or overstate how much is left.
  const inputBlocks = useMemo(() => inputBlocksOf(definition), [definition]);
  const inputTotal = inputBlocks.length;
  const inputPosition = block
    ? inputBlocks.findIndex(
        candidate => candidate.instanceId === block.instanceId
      ) + 1
    : 0;
  const isLast = index >= blocks.length - 1;
  // A question the operator did not release is frozen. Only a QUESTION: a statement has nothing to freeze and
  // would only look dimmed for no reason.
  const isLocked =
    !!block && renderer?.kind === 'INPUT' && lockedIds.has(block.instanceId);
  // A locked question never holds the respondent back: it was answered when the form was first submitted, and
  // it is not theirs to answer again.
  const canAdvance = block
    ? isLocked || !block.required || isBlockAnswered(block, answers)
    : false;

  const handleChange = useCallback(
    (answer: unknown) => {
      // Once finalizing, the captured answer set is the one being judged — a later edit could only
      // be shown and not submitted, so it is refused rather than accepted and dropped. A language
      // switch in flight gets the same refusal: its flush already captured its own answer set, and
      // an edit landing on top of that would race it with no ordering guarantee. A question the
      // operator did not release is refused for the same reason in spirit: the server keeps its saved
      // answer whatever is sent, so accepting the edit would only show a change that never happened.
      if (
        !block ||
        submittingRef.current ||
        switchingLanguageRef.current ||
        lockedIds.has(block.instanceId)
      ) {
        return;
      }
      setAnswers(previous => {
        const next = { ...previous, [block.instanceId]: answer };
        scheduleSave(next);
        return next;
      });
      // A fresh edit means the previous complaint no longer describes the state.
      setRejection(null);
    },
    [block, lockedIds, scheduleSave]
  );

  // Move focus to the new question after navigating, so a screen reader announces where it landed
  // and keyboard focus does not stay on a button that now means something else.
  // Kept in a ref as well as in state so `goTo` can compare against the CURRENT position without
  // taking `index` as a dependency — which would rebuild the callback on every question and, worse,
  // read a stale index if it ever did not.
  const indexRef = useRef(index);
  indexRef.current = index;

  const goTo = useCallback((next: number) => {
    setDirection(next >= indexRef.current ? 'forward' : 'back');
    setIndex(next);
    setRejection(null);
    window.requestAnimationFrame(() => headingRef.current?.focus());
  }, []);

  /**
   * Store whatever is on screen before the switcher is allowed to reload.
   *
   * `LanguageSwitcher` reloads the page as soon as this resolves — the same reload `resolveLocale`
   * already treats as an ordinary re-redeem. Without this, a debounced `scheduleSave` still pending
   * (or in flight) is abandoned on unmount, and the reload brings back the server's older answer,
   * silently replacing what was just entered. A rejection here propagates: the switcher's own
   * sequence only reaches `window.location.reload()` after this resolves, so a failed save keeps the
   * page exactly as it was rather than discarding the answer to a reload anyway.
   *
   * Two things follow from skipping that reload on failure, and both are handled here rather than
   * left implicit:
   *
   * - `MenuItem` switches the app's chrome to the new locale BEFORE ever calling this — so the
   *   moment it does, `locale` is re-synced to match. Without this, a failed flush would leave the
   *   buttons in the new language and the question stuck in the old one, permanently: nothing else
   *   was going to reconcile them once the reload was skipped.
   * - A terminal failure (the invitation was revoked, or already submitted elsewhere) is routed to
   *   its own screen rather than left to silently block the reload and strand the respondent on a
   *   form that can no longer be saved to or switched. This resolves rather than rethrows: the
   *   session is already routed by state, not by the reload, so letting `MenuItem` proceed to
   *   `window.location.reload()` is harmless (a dead invitation re-redeems to the same terminal
   *   outcome) — and rethrowing here would reach `MenuItem`'s own catch, which logs unconditionally
   *   and would report an outcome `routeError` already classified as expected, as a fresh error.
   *
   * The lock mirrors `submittingRef`/`isFinalizing` for the same reason: nothing previously stopped
   * an edit or a submit from starting while this flush was in flight, and two unordered writes with
   * no abort signal between them would leave whichever response landed last as the winner —
   * regardless of which one was actually newer.
   */
  const handleLanguageSelect = useCallback(async () => {
    if (submittingRef.current || switchingLanguageRef.current) {
      return;
    }
    switchingLanguageRef.current = true;
    setIsSwitchingLanguage(true);
    setLocale(getLocaleLanguage() as string);
    try {
      await flushSave(answers);
    } catch (error) {
      if (isTerminalFormError(error)) {
        routeError(error, 'language-switch');
        return;
      }
      throw error;
    } finally {
      switchingLanguageRef.current = false;
      setIsSwitchingLanguage(false);
    }
  }, [answers, flushSave, routeError]);

  const handleSubmit = useCallback(async () => {
    // Claim the lock before the first await, so a double tap cannot start a second finalize. A
    // language switch in flight is refused the same way: its own flush already captured an answer
    // set, and submitting on top of it would race that write with no ordering guarantee.
    if (submittingRef.current || switchingLanguageRef.current) {
      return;
    }
    submittingRef.current = true;
    setIsFinalizing(true);
    try {
      // Store the latest answers before finalizing: submit validates what is STORED, so submitting
      // with a debounce still pending would judge an older answer set than the one on screen.
      await flushSave(answers);
      await submitWithRecovery();
      // Deliberately keep the lock: the invitation is spent, and every path out of here is a screen
      // change rather than a return to the form.
      markSubmitted();
    } catch (error) {
      // Nothing was finalized, so the form goes back to being editable — including for the
      // answer-rejection path below, whose whole point is to send someone back to fix an answer.
      submittingRef.current = false;
      setIsFinalizing(false);
      if (isTerminalFormError(error)) {
        routeError(error, 'submit');
        return;
      }
      const answerRejection = answerRejectionOf(error);
      if (answerRejection) {
        // The server is the authority on validity, so its verdict has to be renderable: jump back
        // to the question it names instead of showing a dead end.
        const target = blocks.findIndex(
          candidate => candidate.instanceId === answerRejection.instanceId
        );
        if (target >= 0) {
          goTo(target);
        }
        setRejection(getTrans('guestForm.error.answerRejected'));
        return;
      }
      setRejection(getTrans('guestForm.error.submitFailed'));
    }
  }, [
    answers,
    blocks,
    flushSave,
    goTo,
    markSubmitted,
    routeError,
    submitWithRecovery
  ]);

  if (!block || !renderer) {
    // Unreachable: an empty definition cannot be published, and unknown types are caught before the
    // runner mounts. Rendering nothing beats rendering a broken frame around nothing.
    return null;
  }

  const BlockComponent = renderer.component;

  return (
    <Box sx={signalAppFrame}>
      {/* ── fixed: identity and progress ─────────────────────────────────── */}
      <Box sx={signalTopBar}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{ minHeight: 44 }}
        >
          {/* Back lives in the bar rather than beside the primary action. Two full-width buttons
              at the bottom compete; up here it is where a phone user already reaches for it, and
              it leaves exactly one obvious move at the bottom of the screen. */}
          <ButtonBase
            // Clamped: the button is hidden on the first question rather than removed, and a
            // hidden control is still reachable programmatically. Reaching -1 would leave `blocks[index]`
            // undefined and the runner rendering nothing at all.
            onClick={() => goTo(Math.max(0, index - 1))}
            aria-label={getTrans('guestForm.back')}
            data-testid="form-back"
            // Leaving the last question mid-finalize would swap the screen under a write that is
            // about to end the form anyway; the same is true mid-language-switch.
            disabled={isFinalizing || isSwitchingLanguage || blockBusy}
            sx={{
              width: 40,
              height: 40,
              ml: -1.125,
              flex: 'none',
              borderRadius: 999,
              color: 'text.secondary',
              // Hidden rather than removed on the first question, so the title does not shift
              // sideways the moment someone moves off it.
              visibility: index > 0 ? 'visible' : 'hidden',
              '&:active': { backgroundColor: 'action.hover' }
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 19 }} aria-hidden="true" />
          </ButtonBase>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontSize: 14.5,
                fontWeight: 600,
                letterSpacing: '-0.02em',
                color: 'text.primary'
              }}
            >
              {data.title}
            </Typography>
            {/* "Question X of Y" is wrong on a CONTENT screen — a statement is not a question, and
                counting it would inflate the total. The segments below still track the whole flow;
                only the wording is withheld where it would misdescribe the screen. */}
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
              {renderer?.kind === 'INPUT'
                ? getTransWithParams('guestForm.progress.position', {
                    current: inputPosition,
                    total: inputTotal
                  })
                : ''}
            </Typography>
          </Box>

          {/* A live region: "saved" has to be announced, not merely seen. */}
          <Typography
            sx={{
              ...signalMono,
              flex: 'none',
              fontSize: 11.5,
              color: saveState === 'failed' ? 'warning.main' : 'text.disabled'
            }}
            role="status"
            aria-live="polite"
            data-testid="form-save-state"
          >
            {saveState === 'saving' && getTrans('guestForm.save.saving')}
            {saveState === 'saved' && getTrans('guestForm.save.saved')}
            {saveState === 'failed' && getTrans('guestForm.save.failed')}
          </Typography>

          {/* Top right, the same flag dropdown the CV builder puts in its header — same component,
              so the two guest flows offer language in one place and one shape.

              Selecting flushes the pending answer, then reloads the page (the switcher's own
              behaviour, unchanged). Mid-form that is safe rather than lucky: answers are autosaved
              server-side and the provider re-redeems the link from the URL, which is the ordinary
              reload path this flow already supports. Withheld while finalizing for the same reason
              editing is: the answer set submit captured must not be able to move under it. Withheld
              while already switching too, so a second tap cannot start a second, unordered flush. */}
          {canSwitchLanguage &&
            !isFinalizing &&
            !isSwitchingLanguage &&
            !blockBusy && (
              <Box
                sx={{ flex: 'none', mr: -1 }}
                data-testid="form-language-switcher"
              >
                <LanguageSwitcher onSelect={handleLanguageSelect} />
              </Box>
            )}
        </Stack>

        {/* One segment per screen. A continuous bar says how far along you are; segments also say
            how many are left, which is the question someone actually has on a form. */}
        <Box
          sx={[signalProgressTrack, signalMeasure]}
          role="progressbar"
          aria-label={getTrans('guestForm.progress.label')}
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          {blocks.map((segment, position) => (
            <Box
              key={segment.instanceId}
              sx={signalProgressSegment(segmentState(position, index))}
            />
          ))}
        </Box>
      </Box>

      {/* ── the only scrolling region ────────────────────────────────────── */}
      {/* Answers can be personal, so they are masked in session replay — the same treatment the
          guest CV gets. 'mask-user-input' covers typing but not rendered text. */}
      {/* Frozen while finalizing. `handleChange` already refuses a late edit, so this is the
          affordance rather than the guarantee: a control that still answers the touch while the
          answer it produces is being discarded would be the confusing half of the same bug. Done
          here, at the one region that holds the question, because the alternative is a `disabled`
          prop threaded through the renderer contract and all eleven block types. */}
      <Box
        data-dd-privacy="mask"
        sx={[
          signalScrollArea,
          (isFinalizing || isSwitchingLanguage) && {
            pointerEvents: 'none',
            opacity: 0.6
          }
        ]}
        ref={headingRef}
        tabIndex={-1}
      >
        {/* Keyed on the block so React remounts it, which is what actually replays the entrance
            animation — without the key the element is reused and the screen would swap silently. */}
        <Box
          key={block.instanceId}
          sx={[signalScreen(direction), signalMeasure]}
        >
          {/* After an operator reopened a submitted response: say which questions are for changing and
              which are only shown. A statement says nothing, so it gets no note. */}
          {isReopened && renderer.kind === 'INPUT' && (
            // The note brings its own top margin; the wrapper cancels it and puts the gap below instead.
            <Box sx={{ mt: -1.75, mb: 3 }}>
              <Typography
                role="note"
                data-testid={isLocked ? 'form-locked-note' : 'form-update-note'}
                sx={signalNote(isLocked ? 'neutral' : 'caution')}
              >
                {getTrans(
                  isLocked
                    ? 'guestForm.reopened.locked'
                    : 'guestForm.reopened.update'
                )}
              </Typography>
            </Box>
          )}

          {isLocked ? (
            /* Frozen from outside, like the question area while finalizing, rather than a `disabled` threaded
               through every renderer. The fieldset disables every native control, keyboard included, and reads as
               disabled to a screen reader without hiding the answer (which `inert` would); `pointerEvents` covers
               what is not a native control. Neither is the guarantee: `handleChange` refuses the edit, and the server
               keeps the saved answer whatever it is sent. */
            <Box
              component="fieldset"
              disabled
              data-testid="form-locked-block"
              sx={{
                border: 0,
                m: 0,
                p: 0,
                minWidth: 0,
                pointerEvents: 'none',
                opacity: 0.7
              }}
            >
              <BlockComponent
                block={block}
                answer={answers[block.instanceId]}
                onChange={handleChange}
                locale={locale}
                onBusyChange={setBlockBusy}
                readOnly
              />
            </Box>
          ) : (
            <BlockComponent
              block={block}
              answer={answers[block.instanceId]}
              onChange={handleChange}
              locale={locale}
              onBusyChange={setBlockBusy}
            />
          )}

          {rejection && (
            <Alert
              severity="warning"
              data-testid="form-rejection"
              sx={{ mt: 3 }}
            >
              {rejection}
            </Alert>
          )}
        </Box>
      </Box>

      {/* ── fixed: one obvious move ──────────────────────────────────────── */}
      <Box sx={signalActionBar}>
        <Box sx={signalMeasure}>
          {isLast ? (
            <AppButton
              fullWidth
              disabled={
                !canAdvance || isFinalizing || isSwitchingLanguage || blockBusy
              }
              onClick={() => void handleSubmit()}
              testId="form-submit"
              dataActionName="guest_form_submit"
              sx={signalPrimaryAction}
            >
              {getTrans(
                isFinalizing ? 'guestForm.submitting' : 'guestForm.submit'
              )}
            </AppButton>
          ) : (
            <AppButton
              fullWidth
              disabled={!canAdvance || isSwitchingLanguage || blockBusy}
              onClick={() => goTo(index + 1)}
              testId="form-next"
              dataActionName="guest_form_next"
              sx={signalPrimaryAction}
            >
              {getTrans('guestForm.next')}
            </AppButton>
          )}
        </Box>
      </Box>
    </Box>
  );
};
